import { useDispatch, useSelector } from 'react-redux';
import {
  alarmIdSelector, updateAlarm, updateAlarmAsset,
} from 'features/details';
import { api, useWebSocketContext } from 'lib';
import { useCallback, useEffect } from 'react';
import {
  addCallId,
  closeHelperCall,
  callIdsSelector,
  removeAlarmCallEvent,
  removeCallEvent,
  removeParticipant,
  upsertAlarmCallEvent,
  upsertAlarmCallParticipant,
  upsertCallEvent,
  refreshLogEvent,
  upsertLogEvent,
  upsertParticipant,
} from 'features/details/chat';
import {
  AlarmCall, AlarmCallParticipant, AlarmCallType, AlarmLogEvent,
} from '@x-guard/xgac-types/xgac';
import { PopulatedAlarm, PopulatedAlarmProfessionalResponder, RS } from 'types';
import {
  endPhoneCall,
  joinPhoneCall,
  openAlarmCallIdsSelector,
  refreshPhoneParticipant,
  removePhoneParticipant,
  upsertPhoneParticipant,
} from 'features/details/phoneCalls';
import { addHelper, updateHelper } from 'features/details/helpers';
import { createResponder, updateResponder } from 'features/details/response/professional';

export const useAlarmDetailsSubscription = () => {

  const alarmId = useSelector(alarmIdSelector);
  // Not loaded yet right after navigating to the alarm (value is null then).
  const customerId = useSelector((state: RS) => state.details.value?.alarm?.customer?._id);
  const callIds = useSelector(callIdsSelector);
  const alarmCallIds = useSelector(openAlarmCallIdsSelector);
  const { ws, retryIndex } = useWebSocketContext();
  const dispatch = useDispatch();

  const revealPhoneParticipant = useCallback((document: AlarmCallParticipant) => {

    dispatch(upsertPhoneParticipant(document));

    // The stream hides the operator name, REST only shows it to the alarm center.
    if (document.ref?._ref !== 'User' || document.name) return;

    dispatch(refreshPhoneParticipant({ alarmCallId: document.alarmCall._id, participantId: document._id }));

  }, [dispatch]);

  /**
   * Show a call that just started: remember a helper call id (subscribes to its participants), request the join code and
   * hand the participants that were already in the call to the chat (the stream only reports changes from now on,
   * without them the helpers list has no connected time for people who joined first).
   */
  const announceCall = useCallback(async (call: AlarmCall, controller: AbortController) => {

    if (call.type === AlarmCallType.HelperCall) dispatch(addCallId(call._id));

    if (call.endedAt) {

      dispatch(endPhoneCall(call._id));
      return;

    }

    try {

      const { participants } = await dispatch(joinPhoneCall({ call, controller }) as any).unwrap();

      participants.forEach((participant: AlarmCallParticipant) => dispatch(upsertParticipant(participant)));

    } catch {

      // Joining failed: the call just does not show, same as before.

    }

  }, [dispatch]);

  useEffect(() => {

    if (!alarmId || !ws) return undefined;

    const revealLogEvent = (document: AlarmLogEvent) => {

      dispatch(upsertLogEvent(document));

      const { createdBy } = document;

      if (createdBy?._ref !== 'User' || createdBy.name) return;

      dispatch(refreshLogEvent({ alarmId: document.alarm._id, eventId: document._id }));

    };

    // Aborted when the alarm changes, so a join request that is still in flight cannot land in the next alarm.
    const controller = new AbortController();
    const stop = ws.subscribe('Alarm', {
      events: ['create', 'update', 'remove'],
      id: [alarmId],
      handler: {
        Asset: {
          update: ({ document }) => dispatch(updateAlarmAsset(document)),
        },
        AlarmHelper: {
          update: ({ document }) => dispatch(updateHelper(document)),
          create: ({ document }) => dispatch(addHelper(document)),
        },
        AlarmLogEvent: {
          create: ({ document }) => revealLogEvent(document),
          update: ({ document }) => revealLogEvent(document),
        },
        AlarmCall: {
          update: ({ document }) => {

            if (document.type === AlarmCallType.HelperCall && document.endedAt) {

              dispatch(closeHelperCall(document._id));

            }

            dispatch(document.endedAt ? endPhoneCall(document._id) : joinPhoneCall({ call: document, controller }));

          },
        },
        Alarm: {
          update: ({ document }) => dispatch(updateAlarm(document as PopulatedAlarm)),
        },
        AlarmProfessionalResponder: {
          update: ({ document }) => dispatch(updateResponder(document)),
          create: ({ document }) => dispatch(createResponder(document as PopulatedAlarmProfessionalResponder)),
        },
      },
    });

    return () => {

      controller.abort();
      stop();

    };

  }, [alarmId, ws, dispatch, retryIndex]);

  // AlarmCall is not a sub entity of Alarm on the websocket, so a call that starts while the alarm is open
  // only arrives as a customer wide create. That message is partial (_id and customer), so load the call and check it is ours.
  useEffect(() => {

    if (!alarmId || !customerId || !ws) return undefined;

    const controller = new AbortController();
    const stop = ws.subscribe('AlarmCall', {
      customers: [customerId],
      events: ['create'],
      handler: {
        AlarmCall: {
          create: async ({ document }) => {

            const { data } = await api.get<AlarmCall>(`/alarm-calls/${document._id}`, { signal: controller.signal });

            if (data.alarms?.some(({ _id }) => _id === alarmId)) announceCall(data, controller);

          },
        },
      },
    });

    return () => {

      controller.abort();
      stop();

    };

  }, [alarmId, customerId, ws, retryIndex, announceCall]);

  useEffect(() => {

    if (!ws || callIds.length === 0) return undefined;

    const stops = callIds.map((callId) => ws.subscribe('AlarmCall', {
      events: ['create', 'update', 'remove'],
      id: [callId],
      handler: {
        // The websocket server does not pass AlarmCall updates on through the Alarm subscription, only here.
        AlarmCall: {
          update: ({ document }) => {

            if (!document.endedAt) return;

            dispatch(closeHelperCall(document._id));
            dispatch(endPhoneCall(document._id));

          },
        },
        AlarmCallEvent: {
          create: ({ document }) => dispatch(upsertCallEvent(document)),
          update: ({ document }) => dispatch(upsertCallEvent(document)),
          remove: ({ document }) => dispatch(removeCallEvent(document._id)),
        },
        AlarmCallParticipant: {
          create: ({ document }) => {

            dispatch(upsertParticipant(document));
            revealPhoneParticipant(document);

          },
          update: ({ document }) => {

            dispatch(upsertParticipant(document));
            revealPhoneParticipant(document);

          },
          remove: ({ document }) => {

            dispatch(removeParticipant(document._id));
            dispatch(removePhoneParticipant(document._id));

          },
        },
      },
    }));

    return () => {

      stops.forEach((stop) => stop());

    };

  }, [callIds, dispatch, retryIndex, revealPhoneParticipant, ws]);

  // Participants of the (non helper) alarm calls we can join by phone. Helper calls are covered above.
  // Known limit: this follows the open calls, so recording chunks uploaded after a call ended show after a reload.
  useEffect(() => {

    if (!ws || !alarmCallIds) return undefined;

    const stops = alarmCallIds.split(',').map((callId) => ws.subscribe('AlarmCall', {
      events: ['create', 'update', 'remove'],
      id: [callId],
      handler: {
        AlarmCall: {
          update: ({ document }) => {

            if (document.endedAt) dispatch(endPhoneCall(document._id));

          },
        },
        AlarmCallEvent: {
          create: ({ document }) => dispatch(upsertAlarmCallEvent(document)),
          update: ({ document }) => dispatch(upsertAlarmCallEvent(document)),
          remove: ({ document }) => dispatch(removeAlarmCallEvent(document._id)),
        },
        AlarmCallParticipant: {
          // Also kept in the chat state, so the log still knows who they were once the call ended.
          create: ({ document }) => {

            dispatch(upsertAlarmCallParticipant(document));
            revealPhoneParticipant(document);

          },
          update: ({ document }) => {

            dispatch(upsertAlarmCallParticipant(document));
            revealPhoneParticipant(document);

          },
          remove: ({ document }) => dispatch(removePhoneParticipant(document._id)),
        },
      },
    }));

    return () => {

      stops.forEach((stop) => stop());

    };

  }, [alarmCallIds, dispatch, retryIndex, revealPhoneParticipant, ws]);

};
